import { ClipboardCheck, FileText, Globe2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ApiError } from '@/lib/api'
import type { SiteHealthAuditScope } from '@/lib/siteHealth.types'
import { useSubmitSiteHealthAudit } from '@/lib/useSiteHealth'
import { useAnalytics } from '@/provider/useAuth'
import type { SiteHealthAuditFormProps } from './siteHealthComponents.types'

const PAGE_LIMIT = 100

function isSiteHealthAuditScope(value: string): value is SiteHealthAuditScope {
  return value === 'site' || value === 'single_page'
}

function messageForError(error: unknown): string {
  const status = error instanceof ApiError ? error.status : null

  if (status === 409)
    return 'You already have a Site Health audit running for this organization.'

  if (status === 429)
    return 'You have reached your Site Health audit allowance for this period.'

  return 'Site Health could not start this audit. Check the URL and try again.'
}

export default function SiteHealthAuditForm({
  disabled = false,
}: SiteHealthAuditFormProps) {
  const [url, setUrl] = useState('')
  const [scope, setScope] = useState<SiteHealthAuditScope>('site')
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  )
  const mutation = useSubmitSiteHealthAudit()
  const analytics = useAnalytics()
  const controlsDisabled = disabled || mutation.isPending

  async function submit() {
    if (controlsDisabled) return

    const trimmedUrl = url.trim()

    if (!trimmedUrl) {
      setValidationMessage('Enter a public website address to audit.')
      return
    }

    setValidationMessage(null)

    try {
      const response = await mutation.mutateAsync({ url: trimmedUrl, scope })

      analytics.track('site_health_audit_submitted', {
        auditId: response.auditId,
        scope,
      })
    } catch {
      // The mutation error is rendered below with a safe status-specific message.
    }
  }

  const errorMessage =
    !disabled && mutation.error ? messageForError(mutation.error) : null

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ClipboardCheck className="size-5" aria-hidden="true" />
        </span>
        <h2 className="text-xl font-semibold tracking-tight">
          Start a new audit
        </h2>
      </div>

      <form
        className="mt-6 grid gap-5 lg:grid-cols-[minmax(260px,1fr)_minmax(420px,1.3fr)_240px] lg:items-end"
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <div>
          <label
            htmlFor="site-health-url"
            className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-primary"
          >
            Website URL
          </label>
          <Input
            id="site-health-url"
            name="url"
            type="text"
            inputMode="url"
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
            placeholder="acme.com"
            autoComplete="url"
            disabled={controlsDisabled}
            aria-invalid={Boolean(validationMessage || errorMessage)}
            className="h-14 rounded-lg"
          />
        </div>

        <fieldset>
          <legend className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-primary">
            Audit scope
          </legend>
          <RadioGroup
            value={scope}
            onValueChange={(value) => {
              if (isSiteHealthAuditScope(value)) setScope(value)
            }}
            disabled={controlsDisabled}
            className="grid h-14 grid-cols-2 gap-0 overflow-hidden rounded-lg border border-border"
          >
            <label
              className={`flex items-center gap-3 border-r border-border px-4 transition-colors ${controlsDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${scope === 'site' ? 'bg-primary/5 text-primary' : 'hover:bg-muted/40'}`}
            >
              <RadioGroupItem
                value="site"
                aria-label="Site"
                className="sr-only"
              />
              <Globe2 className="size-5 shrink-0" aria-hidden="true" />
              <span>
                <span className="block text-sm font-medium">Site</span>
                <span className="block text-xs text-muted-foreground">
                  Audit up to {PAGE_LIMIT} pages
                </span>
              </span>
            </label>
            <label
              className={`flex items-center gap-3 px-4 transition-colors ${controlsDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${scope === 'single_page' ? 'bg-primary/5 text-primary' : 'hover:bg-muted/40'}`}
            >
              <RadioGroupItem
                value="single_page"
                aria-label="Single page"
                className="sr-only"
              />
              <FileText className="size-5 shrink-0" aria-hidden="true" />
              <span>
                <span className="block text-sm font-medium">Single page</span>
                <span className="block text-xs text-muted-foreground">
                  Audit a specific page
                </span>
              </span>
            </label>
          </RadioGroup>
        </fieldset>

        <div className="space-y-2">
          <Button
            type="submit"
            disabled={controlsDisabled}
            size="lg"
            className="h-14 w-full rounded-lg"
          >
            {mutation.isPending && (
              <Loader2
                className="size-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            {mutation.isPending ? 'Starting audit…' : 'Run Site Health'}
          </Button>

          {disabled && (
            <p className="text-center text-sm text-primary">
              One audit can run at a time.
            </p>
          )}
        </div>

        {(validationMessage || errorMessage) && (
          <p
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive lg:col-span-3"
          >
            {validationMessage ?? errorMessage}
          </p>
        )}
      </form>
    </section>
  )
}
